import { Button, Modal, Text } from '@ksyos/design-system';
import { useState } from 'react';
import type { ValidationErrors } from '../../../utils/validation';
import type { IPractitioner } from '../../interfaces/practitioner';
import { ToastType } from '../../../../common/enums/toastType';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { requestMessageBaseValidationSchema } from '../../../../common/validation/requestMessage';
import { postJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { showToast } from '../../../utils/showToast';
import { emptyValidationErrors, updateValidationErrorsForOne } from '../../../utils/validation';
import { FormParagraph } from '../../elements/common';
import { FormLabel } from '../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../elements/forms/InputContainer';
import { TextAreaInput } from '../../fields/TextArea';

interface IRequestMessageSendModalProps {
    alternateCloseButtonText?: string;
    hideHeaderCloseButton?: boolean;
    isMessageRequired?: boolean;
    isOpen: boolean;
    messagePlaceholder?: string;
    onClose: () => void;
    onSent: () => void;
    requestId: string;
    sendButtonText?: string;
    subTitle?: string;
    title?: string;
    toPractitioner: IPractitioner;
}

export function SendRequestMessageModal(props: IRequestMessageSendModalProps) {
    const [message, setMessage] = useState<string | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const isMessageRequired = props.isMessageRequired === undefined || props.isMessageRequired;

    return (
        <Modal
            closeOnOverlayClick={true}
            isOpen={props.isOpen}
            onClose={props.onClose}
            size="medium"
        >
            <Modal.Header className="automation-modal-title">
                {props.title || translateKey('requestMessage.sendAMessage')}
            </Modal.Header>
            {!props.hideHeaderCloseButton && <Modal.CloseButton />}
            <Modal.Body>
                <Text>{props.subTitle}</Text>

                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('requestMessage.sendTo')}</FormLabel>
                    <InputContainer>
                        <FormParagraph>{getDisplayName(props.toPractitioner)}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>

                <HorizontalFormGroupRead>
                    <FormLabel isRequired={isMessageRequired}>{translateKey('requestMessage.message')}</FormLabel>
                    <InputContainer>
                        <TextAreaInput
                            automation="remarkRequestMessage"
                            error={errors.get('message')}
                            maxLength={1000}
                            onChange={(value) => {
                                setMessage(value);
                                setErrors(emptyValidationErrors);
                            }}
                            onDoValidate={() => {
                                if (isMessageRequired) {
                                    setErrors(
                                        updateValidationErrorsForOne(
                                            requestMessageBaseValidationSchema,
                                            { message },
                                            errors,
                                            'message'
                                        )
                                    );
                                }
                            }}
                            placeholder={props.messagePlaceholder || translateKey('common.remarksOrComments')}
                            value={message}
                        />
                    </InputContainer>
                </HorizontalFormGroupRead>
            </Modal.Body>

            <Modal.Footer className="automation-modal-footer">
                <Button
                    onClick={props.onClose}
                    variant="outline"
                >
                    {props.alternateCloseButtonText || translateKey('common.cancel')}
                </Button>
                <Button
                    className="automation-saveSend"
                    isDisabled={errors.size > 0}
                    onClick={saveMessage}
                >
                    {props.sendButtonText || translateKey('request.saveSend')}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    async function saveMessage() {
        if (message) {
            await postJson('/api/requestMessages', { requestId: props.requestId, message });

            props.onSent();
            showToast(
                ToastType.success,
                translateKey('toast.requestMessageSend', getDisplayName(props.toPractitioner))
            );
        } else if (!isMessageRequired) {
            props.onSent();
            showToast(
                ToastType.success,
                translateKey('toast.confirmedRequestDefaultMessage', getDisplayName(props.toPractitioner))
            );
        } else {
            if (isMessageRequired) {
                setErrors(
                    updateValidationErrorsForOne(requestMessageBaseValidationSchema, { message }, errors, 'message')
                );
            }
        }
    }
}
